import { faClose } from '@fortawesome/pro-regular-svg-icons/faClose'
import { useSubjectMember, useSubjectUpdate } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { motion } from 'framer-motion'
import { isNil } from 'ramda'
import { useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout/index'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { getMajorMinorVersion, updateMessages } from '@/modules/app/components/app-update-card/update-messages'
import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'

import type { MouseEventHandler } from 'react'

const AppUpdateCard = () => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const subject = useSubjectMember()
  const [{ mutate }, updateSubject] = useSubjectUpdate()
  const theme = useTheme()
  const navigate = useNavigate()

  const majorMinorVersion = getMajorMinorVersion(APP_VERSION)
  const currentUpdateMessage = updateMessages[majorMinorVersion]

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  const handleCardDismiss: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()

    mixpanel.track('app_update_card_cross_click')
    mutate()
      .then((latestActor) =>
        updateSubject({
          meta: {
            ...latestActor?.meta,
            hasSeenAppUpdate: {
              version: APP_VERSION,
              date: new Date().toISOString(),
            },
          },
        }),
      )
      .finally(() => {
        mutate()
      })
  }
  // Render
  // ------------------------------------------------------------------------- /
  const hasSeenAppUpdate = subject?.meta?.hasSeenAppUpdate

  // Check if user has seen this major.minor version already
  if (
    (!isNil(hasSeenAppUpdate) && getMajorMinorVersion(hasSeenAppUpdate.version) === majorMinorVersion) ||
    !currentUpdateMessage
  ) {
    return null
  }

  return (
    <motion.div {...getDropInProps()}>
      <Card>
        <FlexColumn alignItems={'center'} gap={theme.UI.Spacing.Space4}>
          <StCloseContainer>
            <IconButton variant='Tertiary' icon={faClose} size='large' onClick={handleCardDismiss} />
          </StCloseContainer>

          <FlexColumn alignItems={'center'} gap={theme.UI.Spacing.Space1}>
            {currentUpdateMessage.img && (
              <FlexRow justifyContent={'center'} flexGrow={1}>
                <UpdateIcon src={currentUpdateMessage.img} alt='update image' />
              </FlexRow>
            )}

            <H4 noMargin>
              {currentUpdateMessage.title ||
                t({
                  id: 'newestUpdate.care-card.title',
                  message: 'De app is vernieuwd!',
                })}
            </H4>

            {currentUpdateMessage.body && (
              <CenteredText type='BodyMediumRegular'>{currentUpdateMessage.body}</CenteredText>
            )}
          </FlexColumn>
          <Button variant='Primary' type='submit' onClick={() => navigate('/app-update')}>
            {t({
              id: 'newestUpdate.cta',
              message: 'Ontdek de nieuwste updates',
            })}
          </Button>
        </FlexColumn>
      </Card>
    </motion.div>
  )
}

const StCloseContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
`

const UpdateIcon = styled.img`
  min-width: 48px;
`

const CenteredText = styled(Span)`
  text-align: center;
`

export { AppUpdateCard }
